import { CommonModule } from '@angular/common';
import {
  Component,
  forwardRef,
  HostBinding,
  Input,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  CounterTranslationsConfig,
  ExtendedCounterConfig,
  IconTheme,
} from '@rd/types';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Subject,
  withLatestFrom,
} from 'rxjs';
import { IconModule } from '../icon/icon.module';
import { CounterNumberFormatterDirective } from './counter-number-formatter.directive';

export const DEFAULT_COUNTER_CONFIG: ExtendedCounterConfig = {
  minValue: 1,
  maxValue: 2,
  valuePerChange: 1,
  formatValue: false,
  debounceTime: 1000,
  updateOnBlur: false,
};

const DEFAULT_TRANSLATIONS_CONFIG: CounterTranslationsConfig = {
  hintTranslation: '',
  emptyErrorTranslation: '',
  patternErrorTranslation: '',
  minusButtonAriaLabel: '',
  plusButtonAriaLabel: '',
};

@UntilDestroy()
@Component({
  imports: [
    CommonModule,
    IconModule,
    MatFormFieldModule,
    MatInputModule,
    CounterNumberFormatterDirective,
  ],
  selector: 'rd-shared-counter',
  templateUrl: './counter.component.html',
  styleUrls: ['./counter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CounterComponent),
      multi: true,
    },
  ],
})
export class CounterComponent implements ControlValueAccessor, OnInit {
  @HostBinding('class.rd-shared-counter') componentClass = true;

  @Input() set config(counterConfig: Partial<ExtendedCounterConfig>) {
    this._config = { ...DEFAULT_COUNTER_CONFIG, ...counterConfig };
  }

  get config(): ExtendedCounterConfig {
    return this._config;
  }

  private _config = DEFAULT_COUNTER_CONFIG;

  @Input() set translationsConfig(
    counterTranslationsConfig: Partial<CounterTranslationsConfig>,
  ) {
    this._translationsConfig = {
      ...DEFAULT_TRANSLATIONS_CONFIG,
      ...counterTranslationsConfig,
    };
  }

  get translationsConfig(): CounterTranslationsConfig {
    return this._translationsConfig;
  }

  private _translationsConfig = DEFAULT_TRANSLATIONS_CONFIG;

  private counterValueSubject = new BehaviorSubject<{
    shouldEmitChange: boolean;
    value: number;
  }>({
    shouldEmitChange: false,
    value: 0,
  });
  private counterValue$ = this.counterValueSubject.pipe(
    map(({ value }) => value),
  );

  private isDisabledSubject = new BehaviorSubject<boolean>(false);

  subtractCounterValue = new Subject<void>();

  addCounterValue = new Subject<void>();

  private isMinusButtonDisabled$ = combineLatest([
    this.counterValue$,
    this.isDisabledSubject,
  ]).pipe(
    map(
      ([counterValue, isDisabled]) =>
        this.exceedsMinimumValue(counterValue - this.config.valuePerChange) ||
        isDisabled,
    ),
  );

  private isPlusButtonDisabled$ = combineLatest([
    this.counterValue$,
    this.isDisabledSubject,
  ]).pipe(
    map(
      ([counterValue, isDisabled]) =>
        this.exceedsMaximumValue(counterValue + this.config.valuePerChange) ||
        isDisabled,
    ),
  );

  vm$ = combineLatest([
    this.counterValue$,
    this.isDisabledSubject,
    this.isMinusButtonDisabled$,
    this.isPlusButtonDisabled$,
  ]).pipe(
    map(
      ([
        counterValue,
        isDisabled,
        isMinusButtonDisabled,
        isPlusButtonDisabled,
      ]) => ({
        counterValue,
        isDisabled,
        isMinusButtonDisabled,
        isPlusButtonDisabled,
        minusIconTheme: (isMinusButtonDisabled
          ? 'disabled'
          : 'link') as IconTheme,
        plusIconTheme: (isPlusButtonDisabled
          ? 'disabled'
          : 'link') as IconTheme,
        minusIconActiveClass: !this.exceedsMinimumValue(counterValue)
          ? 'icon-active'
          : '',
        plusIconActiveClass: !this.exceedsMaximumValue(counterValue)
          ? 'icon-active'
          : '',
        ariaInputLabel: this.config.aria_unit
          ? `${counterValue} ${this.config.aria_unit}`
          : counterValue,
      }),
    ),
  );

  onTouched: () => void;

  ngOnInit(): void {
    this.subtractCounterValue
      .pipe(
        withLatestFrom(this.counterValue$, this.isMinusButtonDisabled$),
        filter(([, , isMinusButtonDisabled]) => !isMinusButtonDisabled),
        untilDestroyed(this),
      )
      .subscribe(([, counterValue]) => {
        this.counterValueSubject.next({
          value: counterValue - this.config.valuePerChange,
          shouldEmitChange: true,
        });
      });

    this.addCounterValue
      .pipe(
        withLatestFrom(this.counterValue$, this.isPlusButtonDisabled$),
        filter(([, , isPlusButtonDisabled]) => !isPlusButtonDisabled),
        untilDestroyed(this),
      )
      .subscribe(([, counterValue]) => {
        this.counterValueSubject.next({
          value: counterValue + this.config.valuePerChange,
          shouldEmitChange: true,
        });
      });
  }

  /*
    ====================================
      CVA functions
    ====================================
  */

  writeValue(val: number): void {
    this.counterValueSubject.next({
      value: val,
      shouldEmitChange: false,
    });
  }

  registerOnChange(onChanged: (value: number) => void): void {
    this.counterValueSubject
      .pipe(
        filter(({ shouldEmitChange }) => shouldEmitChange),
        map(({ value }) => value),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe(onChanged);
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabledSubject.next(isDisabled);
  }

  /*
    ====================================
      Utils functions
    ====================================
  */

  private exceedsMinimumValue(value: number): boolean {
    return value < this.config.minValue;
  }

  private exceedsMaximumValue(value: number): boolean {
    return value > this.config.maxValue;
  }

  onCounterValueChange(value: number) {
    this.counterValueSubject.next({
      value,
      shouldEmitChange: true,
    });
  }
}
